// Functions for reading and writing statements from the Firestore database.

import { firestore as db } from "./firebase";
import { NameValuePair } from "./models";

// The names of the Firestore collections used in this file.
export const COLLECTION_STATEMENTS: string = "statements";

// All possible statuses a statement can have.
const STATUS_PUBLISHED = "published";
const STATUS_DRAFT = "draft";

// The Statement interface defines how a statement is stored in the Firestore database.
export interface Statement {
  id: string;
  slug: string;
  title: string;
  description?: string;
  category?: string;
  status: string;
  continent: string;
  country: string | null;
  region: string | null;
  municipality: string | null;
  links: NameValuePair[];
  hasBackgroundImage: boolean;
  imgUrl: any | null;
}

// A type for displaying statements in a list where not all properties are needed.
export interface StatementListItem {
  id: string;
  slug: string;
  title: string;
}

export interface StatementList {
  id: string;
  slug: string;
  title: string;
  imgUrl: any;
}

export async function getStatements(isAdmin: boolean): Promise<Statement[]> {
  let query = db
    .collection(COLLECTION_STATEMENTS)
    .where("status", "in", getStatuses(isAdmin));

  const data = await query.get();

  let statements: Statement[] = [];
  data.forEach((doc) => statements.push({ ...doc.data() } as any));
  return statements;
}

export async function getStatementBySlug(
  slug: string,
  isAdmin: boolean
): Promise<Statement | null> {
  const data = await db
    .collection(COLLECTION_STATEMENTS)
    .where("slug", "==", slug)
    .where("status", "in", getStatuses(isAdmin))
    .limit(1)
    .get();

  let statements: Statement[] = [];
  data.forEach((doc) => statements.push({ ...doc.data() } as any));

  return statements && statements.length > 0 ? statements[0] : null;
}

export async function getNewestStatements(
  limit: number = 10
): Promise<Statement[]> {
  const data = await db
    .collection(COLLECTION_STATEMENTS)
    .where("status", "in", getStatuses(false))
    .orderBy("timestamp", "desc")
    .limit(limit)
    .get();

  let statements: Statement[] = [];
  data.forEach((doc) => statements.push({ ...doc.data() } as any));
  return statements;
}

export function getStatementSnapshot(
  statementId: string,
  isAdmin: boolean,
  setStatement: (statement: Statement | null) => void
): () => void {
  return db
    .collection(COLLECTION_STATEMENTS)
    .doc(statementId)
    .onSnapshot(function (doc) {
      const statement = doc.data() as any;
      if (statement && getStatuses(isAdmin).indexOf(statement.status) !== -1) {
        setStatement(statement);
      } else setStatement(null);
    });
}

export function getStatuses(isAdmin: boolean) {
  return isAdmin ? [STATUS_DRAFT, STATUS_PUBLISHED] : [STATUS_PUBLISHED];
}

export function mapToListItem(statement: Statement): StatementListItem {
  return {
    id: statement.id,
    slug: statement.slug,
    title: statement.title,
  };
}
